// InRange's answer to Amazon marking an item received, or not theirs to give: Amazon's
// words as they gave them, the keeper's view right above the choice, and Approve, Don't
// approve or Ask Amazon for more, each opening a box before anything is sent. The option the
// keeper's view points to is marked Recommended. No Skip: it has to be answered
// (docs/Y2026-Q4/design-amazon-item-owners.md section 2.6).
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useState } from "react";

import {
  amazonAnswerSubject,
  type FeedbackQuestion,
  useAnswerFeedbackQuestion,
} from "../api/hooks/useAgentFeedback";
import { agentDisplayLabel } from "../utils/runParticipants";
import { Cited } from "./Cited";
import styles from "./NextActionTable.module.css";
import own from "./AgentFeedbackSection.module.css";

const APPROVE = 0;
/** Option index for each recommendation the keeper may make (amazon_item_answers.RECOMMENDS). */
const RECOMMENDED: Record<string, number> = {
  approve: 0,
  "don't approve": 1,
  "ask for more": 2,
};

export const AmazonAnswerForm = ({ question }: { question: FeedbackQuestion }) => {
  const { user } = useKindeAuth();
  const answer = useAnswerFeedbackQuestion();
  const subject = amazonAnswerSubject(question);
  const view = subject?.view ?? null;
  const recommended = view ? RECOMMENDED[view.recommend] : null;
  const [picked, setPicked] = useState<number | null>(null);
  const [text, setText] = useState("");
  if (!subject) return null;
  const agent = agentDisplayLabel(question.agent);
  const pick = (i: number) => {
    setPicked(i);
    // A reply or a question comes drafted by the keeper, for InRange to edit.
    setText(i !== APPROVE && view?.draft ? view.draft : "");
  };
  const ready = picked !== null && (picked === APPROVE || text.trim() !== "");
  const submit = () => {
    if (!ready || picked === null || answer.isLoading) return;
    answer.mutate({
      question_id: question.question_id,
      options: [picked],
      other: text.trim(),
      actor_email: user?.email ?? "",
    });
  };
  return (
    <div className={own.answer}>
      <section className={styles.section}>
        <h5>Amazon said</h5>
        {subject.exchange.map((e, i) => (
          <p key={i}>
            {e.name} ({e.by}), {e.when}
            {e.text ? `: “${e.text}”` : ""}
          </p>
        ))}
      </section>
      <section className={`${styles.section} ${styles.closedBox}`}>
        <h5>
          {agent}&apos;s view
          {view ? `: ${view.verdict}` : ""}
        </h5>
        {subject.check === "pending" && (
          <p className={styles.small}>
            {agent} is checking the records and the other buildings. Its view shows here when
            it is done; you can answer before then.
          </p>
        )}
        {subject.check === "failed" && (
          <p className={styles.small}>
            {agent} could not check it{subject.check_note ? `: ${subject.check_note}` : "."}{" "}
            Answer on what you know.
          </p>
        )}
        {view && (
          <>
            <p>
              <strong>Records:</strong> <Cited text={view.records} inline />
            </p>
            {view.other_sites && (
              <p>
                <strong>Other sites:</strong> <Cited text={view.other_sites} inline />
              </p>
            )}
          </>
        )}
      </section>
      <h5>Choose one</h5>
      <div className={own.options}>
        {question.options.map((option, i) => (
          <label key={i} className={`${own.option} ${picked === i ? own.picked : ""}`}>
            <input
              type="radio"
              name={question.question_id}
              checked={picked === i}
              disabled={answer.isLoading}
              onChange={() => pick(i)}
            />
            <span>
              <span className={own.optionLabel}>
                {option.label}
                {recommended === i && <span className={own.recommended}> Recommended</span>}
              </span>
              <span className={own.optionDescription}>
                {recommended === i && view?.why ? view.why : option.description}
              </span>
            </span>
          </label>
        ))}
      </div>
      {picked !== null && (
        <textarea
          className={own.other}
          aria-label={picked === APPROVE ? "A note, optional" : "Your reply to Amazon"}
          placeholder={
            picked === APPROVE
              ? "A note, optional. It is kept in the item's history, which Amazon sees."
              : picked === 1
                ? "Your reply to Amazon, shown on the item."
                : "Your question to Amazon, shown on the item."
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          maxLength={2000}
        />
      )}
      <div className={own.submitRow}>
        <button
          type="button"
          className={`${styles.button} ${styles.primary}`}
          disabled={!ready || answer.isLoading}
          onClick={submit}
        >
          {answer.isLoading ? "Sending…" : picked === APPROVE ? "Approve" : "Send to Amazon"}
        </button>
        <span className={styles.small}>
          {picked === null
            ? "Choose an option to continue."
            : picked !== APPROVE && !text.trim()
              ? "Write your reply to continue."
              : "It is applied as soon as you answer."}
        </span>
      </div>
      {answer.error && <p className={styles.error}>{answer.error.message}</p>}
    </div>
  );
};
